import { type RefObject, useLayoutEffect } from 'react'

/**
 * Keeps a chat from getting shorter: when content leaves its bottom (an answered question's input),
 * `spacer` takes up the height, so nothing above slides down and what comes next fills the room.
 */
export function useChatHoldHeight(
  scroller: RefObject<HTMLElement | null>,
  content: RefObject<HTMLElement | null>,
  spacer: RefObject<HTMLElement | null>
) {
  useLayoutEffect(() => {
    const scrollNode = scroller.current
    const contentNode = content.current
    const spacerNode = spacer.current
    if (!scrollNode || !contentNode || !spacerNode) return

    let floor = contentNode.offsetHeight
    let width = contentNode.offsetWidth
    let scrollTop = scrollNode.scrollTop
    const onScroll = () => {
      scrollTop = scrollNode.scrollTop
    }

    const observer = new ResizeObserver(() => {
      const height = contentNode.offsetHeight
      // A new width reflows the text, which isn't content leaving, so the hold starts over.
      if (contentNode.offsetWidth !== width) {
        width = contentNode.offsetWidth
        floor = height
      }
      floor = Math.max(floor, height)
      spacerNode.style.height = `${floor - height}px`
      // The shrink already clamped the scroll before this ran; put it back where the reader left it.
      if (scrollNode.scrollTop < scrollTop) scrollNode.scrollTop = scrollTop
      scrollTop = scrollNode.scrollTop
    })
    observer.observe(contentNode)
    scrollNode.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      observer.disconnect()
      scrollNode.removeEventListener('scroll', onScroll)
    }
  }, [scroller, content, spacer])
}
